import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home, BarChart3, Settings, CreditCard, Calendar, Users, Crown, Shield, List, List as ListIcon, RefreshCw, RefreshCw as RefreshCwIcon, FileText } from 'lucide-react';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);

  useEffect(() => {
    const sessionIdParam = searchParams.get('session_id');
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
      // In a real app, you would verify the session with your backend
      // and fetch the subscription details
      setTimeout(() => {
        setSubscription({
          id: 'sub_1234567890',
          status: 'active',
          plan: {
            name: 'Pro',
            price: 29,
            interval: 'month'
          }
        });
        setLoading(false);
      }, 2000);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleViewSubscription = () => {
    navigate('/account?tab=subscription');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Verifying Payment
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we confirm your subscription...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Payment Successful!
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Welcome to PostSub! Your subscription is now active.
          </p>
        </div>

        {/* Subscription Details */}
        {subscription && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Subscription Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <Crown className="h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {subscription.plan.name} Plan
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  ${subscription.plan.price}/{subscription.plan.interval}
                </p>
              </div>
              
              <div className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Status
                </h3>
                <p className="text-gray-600 dark:text-gray-400 capitalize">
                  {subscription.status}
                </p>
              </div>
              
              <div className="text-center p-6 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <Calendar className="h-8 w-8 text-purple-600 dark:text-purple-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Next Billing
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            What's Next?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Create Your First Post
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Start writing and publishing your newsletter content with our powerful editor.
              </p>
              <button
                onClick={() => navigate('/content')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium flex items-center"
              >
                Start Writing
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            
            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Import Your Audience
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Migrate your existing subscribers from other platforms seamlessly.
              </p>
              <button
                onClick={() => navigate('/migration')}
                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium flex items-center"
              >
                Import Subscribers
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            
            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  View Analytics
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Track your newsletter performance with detailed analytics and insights.
              </p>
              <button
                onClick={() => navigate('/insights')}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium flex items-center"
              >
                View Analytics
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
            
            <div className="p-6 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                  <Settings className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Customize Your Brand
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Set up your custom domain, themes, and branding to make it yours.
              </p>
              <button
                onClick={() => navigate('/brand')}
                className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-700 dark:hover:text-yellow-300 font-medium flex items-center"
              >
                Customize Brand
                <ArrowRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-8">
          <div className="flex items-start space-x-3">
            <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Your Payment is Secure
              </h3>
              <p className="text-blue-800 dark:text-blue-200">
                Your payment information is encrypted and processed securely by Stripe. 
                We never store your card details. You can manage your subscription and 
                billing information anytime from your account settings.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleGoToDashboard}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <Home className="h-5 w-5 mr-2" />
            Go to Dashboard
          </button>
          
          <button
            onClick={handleViewSubscription}
            className="px-8 py-3 bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
          >
            <CreditCard className="h-5 w-5 mr-2" />
            View Subscription
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
